// Billing & Invoices — list, filter, and action invoices.
// Server-driven via DataTable (TASK-N33; PatientsPage/UsersPage precedent).

import { useCallback, useMemo, useState } from "react";
import type { JSX, ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch } from "../lib/api.js";
import { useAuth } from "../hooks/useAuth.js";
import { Shell } from "../components/Shell.js";
import { CreateInvoiceModal } from "../components/CreateInvoiceModal.js";
import { PaymentLinkModal } from "../components/PaymentLinkModal.js";
import { STATUS_PILL, formatRupees, fmtDate, type Invoice } from "../lib/billing.js";
import { downloadCsv } from "../lib/csv-export.js";
import { useHighlightedRow } from "../hooks/useHighlightedRow.js";
import { bulkPatch, reportBulkResult } from "./SrListPage.js";
import { formatPatientRef } from "../lib/refs.js";
import {
  DataTable,
  useDataTable,
  type ColumnDef,
  type ListQuery,
} from "../components/DataTable/index.js";

// Mirror the backend role gate (TASK-N47): only agency_admin can issue/
// pay/cancel from apps/web. super_admin is redirected out by N52, so it
// is intentionally absent here.
const ISSUER_ROLES = new Set(["agency_admin"]);

// docs/21 §8 — "Issue invoice + send link" mirrors the backend's requireCoordinator gate
// on POST /billing/payment-links (super_admin is redirected out of apps/web by N52).
const LINK_ISSUER_ROLES = new Set(["coordinator", "agency_admin"]);

interface InvoiceListResponse { data: Invoice[]; total: number; page: number; limit: number; }

type StatusFilter = "all" | Invoice["status"];

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: "all",       label: "All" },
  { value: "draft",     label: "Draft" },
  { value: "issued",    label: "Issued" },
  { value: "paid",      label: "Paid" },
  { value: "overdue",   label: "Overdue" },
  { value: "cancelled", label: "Cancelled" },
  { value: "refunded",  label: "Refunded" },
  { value: "failed",    label: "Failed" },
];

function tabCls(active: boolean): string {
  return active
    ? "bg-navy-700 text-white"
    : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50";
}

function buildQs(query: ListQuery): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v == null || v === "") continue;
    p.set(k, String(v));
  }
  return p.toString();
}

function useInvoices(query: ListQuery): ReturnType<typeof useQuery<InvoiceListResponse>> {
  const qs = buildQs(query);
  return useQuery<InvoiceListResponse>({
    queryKey: ["invoices", qs],
    queryFn:  () => apiFetch<InvoiceListResponse>(`/billing/invoices?${qs}`),
  });
}

interface PatchActionContext {
  endpoint: string;
  body?: Record<string, string>;
  qc: ReturnType<typeof useQueryClient>;
}

async function patchInvoice(invId: string, ctx: PatchActionContext): Promise<void> {
  await apiFetch(`/billing/invoices/${invId}/${ctx.endpoint}`, {
    method: "PATCH",
    body:   ctx.body ? JSON.stringify(ctx.body) : undefined,
  });
  void ctx.qc.invalidateQueries({ queryKey: ["invoices"] });
}

function StatusBadge({ status }: { status: Invoice["status"] }): JSX.Element {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_PILL[status]}`}>
      {status}
    </span>
  );
}

interface RowActionHandlers {
  onEdit: (inv: Invoice) => void;
  onPatch: (invId: string, endpoint: string, body?: Record<string, string>) => Promise<void>;
}

function RowActions({ inv, h }: { inv: Invoice; h: RowActionHandlers }): JSX.Element {
  return (
    <div className="flex gap-2 flex-wrap">
      {inv.status === "draft" && (
        <button onClick={() => h.onEdit(inv)}
          className="px-2 py-1 text-xs rounded-md bg-slate-50 text-slate-700 hover:bg-slate-100 font-medium">
          Edit
        </button>
      )}
      {inv.status === "draft" && (
        <button onClick={() => { void h.onPatch(inv._id, "issue"); }}
          className="px-2 py-1 text-xs rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium">
          Issue
        </button>
      )}
      {inv.status === "issued" && (
        <button onClick={() => { void h.onPatch(inv._id, "pay", { payment_method: "upi" }); }}
          className="px-2 py-1 text-xs rounded-md bg-green-50 text-green-700 hover:bg-green-100 font-medium">
          Mark Paid
        </button>
      )}
      {(inv.status === "draft" || inv.status === "issued") && (
        <button onClick={() => { void h.onPatch(inv._id, "cancel"); }}
          className="px-2 py-1 text-xs rounded-md bg-red-50 text-red-700 hover:bg-red-100 font-medium">
          Cancel
        </button>
      )}
    </div>
  );
}

function buildColumns(h: RowActionHandlers): ReadonlyArray<ColumnDef<Invoice>> {
  return [
    { accessorKey: "invoice_number", header: "Invoice #", sortable: true,
      cell: (inv) => (
        <Link to={`/billing/${inv._id}`} className="font-mono text-xs text-navy-700 hover:underline">
          {inv.invoice_number}
        </Link>
      ),
    },
    { accessorKey: "patient_id", header: "Patient",
      cell: (inv) => <span className="font-mono text-xs text-slate-500">{formatPatientRef({ _id: inv.patient_id })}</span>,
    },
    { accessorKey: "amount_paise", header: "Amount", sortable: true,
      cell: (inv) => <span className="text-slate-800 font-medium">{formatRupees(inv.amount_paise)}</span>,
    },
    { accessorKey: "status", header: "Status", sortable: true,
      cell: (inv) => <StatusBadge status={inv.status} />,
    },
    { accessorKey: "issued_at", header: "Issued", sortable: true,
      cell: (inv) => <span className="text-slate-500">{fmtDate(inv.issued_at)}</span>,
    },
    { accessorKey: "due_date", header: "Due", sortable: true,
      cell: (inv) => <span className="text-slate-500">{fmtDate(inv.due_date)}</span>,
    },
    { accessorKey: "actions", header: "Actions",
      cell: (inv) => <RowActions inv={inv} h={h} />,
    },
  ];
}

interface IssueDraftsCtx {
  items: readonly Invoice[];
  selectedIds: readonly string[];
  setBusy: (b: boolean) => void;
  clearSelection: () => void;
  refetch: () => Promise<unknown>;
}

async function issueDraftsAction(ctx: IssueDraftsCtx): Promise<void> {
  const draftIds = ctx.items
    .filter((i) => ctx.selectedIds.includes(i._id) && i.status === "draft")
    .map((i) => i._id);
  const skipped = ctx.selectedIds.length - draftIds.length;
  if (draftIds.length === 0) {
    toast.warning(`No drafts in selection (${skipped} skipped — already issued or non-issuable).`);
    return;
  }
  ctx.setBusy(true);
  try {
    const { ok, failed } = await bulkPatch(draftIds, (id) => `/billing/invoices/${id}/issue`);
    const verb = skipped > 0 ? `Issued ${ok} (${skipped} non-draft skipped)` : "Issued";
    reportBulkResult(verb, ok, failed);
    ctx.clearSelection();
    await ctx.refetch();
  } finally { ctx.setBusy(false); }
}

function BillingModals({
  showCreate, showPaymentLink, editingInvoice, onCreated, onCloseCreate, onClosePaymentLink, onCloseEdit,
}: {
  showCreate: boolean;
  showPaymentLink: boolean;
  editingInvoice: Invoice | null;
  onCreated: () => void;
  onCloseCreate: () => void;
  onClosePaymentLink: () => void;
  onCloseEdit: () => void;
}): JSX.Element {
  return (
    <>
      {showCreate && (
        <CreateInvoiceModal onClose={onCloseCreate} onCreated={onCreated} />
      )}
      {showPaymentLink && (
        <PaymentLinkModal onClose={onClosePaymentLink} onIssued={onCreated} />
      )}
      {editingInvoice && (
        <CreateInvoiceModal
          mode="edit"
          initialValues={editingInvoice}
          onClose={onCloseEdit}
          onCreated={() => { onCreated(); onCloseEdit(); }}
        />
      )}
    </>
  );
}

function BillingHeader({ canSendLink, onSendLink, onCreate }: {
  canSendLink: boolean; onSendLink: () => void; onCreate: () => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <h1 className="text-xl font-semibold text-slate-900">Billing &amp; Invoices</h1>
      <div className="flex items-center gap-3">
        {canSendLink && (
          <button onClick={onSendLink}
            className="px-4 py-2 text-sm rounded-lg border border-navy-700 text-navy-700 font-medium hover:bg-navy-50">
            Send payment link
          </button>
        )}
        <button onClick={onCreate}
          className="px-4 py-2 text-sm rounded-lg bg-navy-700 text-white font-medium hover:bg-navy-800">
          + Create Invoice
        </button>
      </div>
    </div>
  );
}

function StatusTabs({ active, onChange }: {
  active: StatusFilter;
  onChange: (v: StatusFilter) => void;
}): JSX.Element {
  return (
    <div className="flex gap-1 flex-wrap" role="tablist" aria-label="Invoice status filter">
      {STATUS_TABS.map((tab) => (
        <button key={tab.value} role="tab" aria-selected={active === tab.value}
          onClick={() => onChange(tab.value)}
          className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${tabCls(active === tab.value)}`}>
          {tab.label}
        </button>
      ))}
    </div>
  );
}

interface BulkActionsDeps {
  items: readonly Invoice[];
  canIssue: boolean;
  busy: boolean;
  setBusy: (b: boolean) => void;
  refetch: () => Promise<unknown>;
  clearSelection: () => void;
}

function exportInvoicesCsv(items: readonly Invoice[], selectedIds: readonly string[]): void {
  const selected = items.filter((i) => selectedIds.includes(i._id));
  downloadCsv(`invoices-${new Date().toISOString().slice(0, 10)}.csv`, [
    { header: "Invoice", value: (i) => i.invoice_number },
    { header: "Patient", value: (i) => i.patient_id },
    { header: "Amount (paise)", value: (i) => i.amount_paise },
    { header: "Status",  value: (i) => i.status },
    { header: "Issued",  value: (i) => i.issued_at ?? "" },
    { header: "Due",     value: (i) => i.due_date ?? "" },
  ], selected);
  toast.success(`Exported ${selected.length} invoice${selected.length === 1 ? "" : "s"}.`);
}

function BulkActionsBar({ deps, selectedIds }: { deps: BulkActionsDeps; selectedIds: readonly string[] }): JSX.Element {
  return (
    <div className="flex items-center gap-3">
      {deps.canIssue && (
        <button type="button" disabled={deps.busy}
          onClick={() => void issueDraftsAction({
            items: deps.items, selectedIds, setBusy: deps.setBusy,
            clearSelection: deps.clearSelection, refetch: deps.refetch,
          })}
          className="text-xs text-navy-800 hover:text-navy-950 underline disabled:opacity-50">
          {deps.busy ? "Issuing…" : "Issue selected drafts"}
        </button>
      )}
      <button type="button" onClick={() => exportInvoicesCsv(deps.items, selectedIds)}
        className="text-xs text-navy-800 hover:text-navy-950 underline">Export CSV</button>
    </div>
  );
}

export function BillingPage(): JSX.Element {
  const { user } = useAuth();
  const role = user?.role ?? "";
  const canIssue = ISSUER_ROLES.has(role);
  const canSendLink = LINK_ISSUER_ROLES.has(role);
  const [showCreate,     setShowCreate]     = useState(false);
  const [showPaymentLink, setShowPaymentLink] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [busy,           setBusy]           = useState(false);
  const { highlightId } = useHighlightedRow();
  const qc = useQueryClient();

  const tableState = useDataTable({
    pageSize: 20,
    defaultSort: { sortBy: "created_at", order: "desc" },
  });

  const statusFilter: StatusFilter =
    (tableState.query["status"] as StatusFilter | undefined) ?? "all";

  const { data, isLoading, isError, refetch } = useInvoices(tableState.query);
  const items: Invoice[] = data?.data ?? [];

  const onPatch = useCallback(async (invId: string, endpoint: string, body?: Record<string, string>): Promise<void> => {
    await patchInvoice(invId, { endpoint, body, qc });
    await refetch();
  }, [qc, refetch]);

  const columns = useMemo<ReadonlyArray<ColumnDef<Invoice>>>(
    () => buildColumns({ onEdit: setEditingInvoice, onPatch }),
    [onPatch],
  );

  const clearSelection = tableState.selection.clear;
  const renderBulkActions = useCallback(
    (ids: readonly string[]): ReactNode => (
      <BulkActionsBar
        deps={{ items, canIssue, busy, setBusy, refetch, clearSelection }}
        selectedIds={ids}
      />
    ),
    [items, canIssue, busy, refetch, clearSelection],
  );

  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <BillingHeader canSendLink={canSendLink}
          onSendLink={() => setShowPaymentLink(true)} onCreate={() => setShowCreate(true)} />

        <StatusTabs active={statusFilter}
          onChange={(v) => tableState.setFilter("status", v === "all" ? undefined : v)} />

        <DataTable<Invoice>
          state={tableState}
          columns={columns}
          data={items}
          total={data?.total ?? 0}
          rowKey={(inv) => inv._id}
          ariaLabel="Invoices"
          isLoading={isLoading}
          isError={isError}
          errorMessage="Failed to load invoices."
          emptyState="No invoices found."
          searchPlaceholder="Search by invoice number or patient"
          selectable
          bulkActions={renderBulkActions}
          highlightId={highlightId ?? undefined}
        />
      </div>

      <BillingModals
        showCreate={showCreate}
        showPaymentLink={showPaymentLink}
        editingInvoice={editingInvoice}
        onCreated={() => void qc.invalidateQueries({ queryKey: ["invoices"] })}
        onCloseCreate={() => setShowCreate(false)}
        onClosePaymentLink={() => setShowPaymentLink(false)}
        onCloseEdit={() => setEditingInvoice(null)}
      />
    </Shell>
  );
}
